import GroupIcon from '@mui/icons-material/Group';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import TranslateIcon from '@mui/icons-material/Translate';
import {
  Box,
  FormControl,
  FormHelperText,
  TextField,
  Typography,
} from '@mui/material';
import { blue, green, grey, red } from '@mui/material/colors';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AxiosConfig from '../../../../../config/AxiosConfig';
import languages from '../../../../helpers/utils/languages';
import useDynamicData from '../../../../hooks/template/useDynamicData';
import { setSnackBar } from '../../../../states/app/staticSlice';
import { ComponentRTE } from '../../../utils/common/ComponentRTE';
import Dropzone from '../../../utils/common/Dropzone';
import { FilterSelect } from '../components/FilterSelect';
import { MonoFilterSelect } from './MonoFilterSelect';
import { TranslateTemplate } from './TranslateTemplate';

// Extraction des labels dynamiques (ex: {{FIRSTNAME}})
const DYN_LABEL_PATTERN = '\\{\\{([a-zA-Z]+)\\}\\}';

const extractLabelsFromString = (value) => {
  const labels = new Set();
  if (typeof value !== 'string') return labels;
  const regex = new RegExp(DYN_LABEL_PATTERN, 'g');
  let match;
  while ((match = regex.exec(value))) {
    labels.add(match[1]);
  }
  return labels;
};

const extractLabelsFromJson = (node) => {
  const labels = new Set();
  if (!node) return labels;
  const visit = (n) => {
    if (!n) return;
    if (Array.isArray(n)) {
      n.forEach(visit);
      return;
    }
    if (typeof n === 'object') {
      if (typeof n.text === 'string') {
        const regex = new RegExp(DYN_LABEL_PATTERN, 'g');
        let match;
        while ((match = regex.exec(n.text))) {
          labels.add(match[1]);
        }
      }
      Object.keys(n).forEach((key) => visit(n[key]));
    }
  };
  visit(node);
  return labels;
};

export const EditNewTemplate = ({
  templateData,
  setTemplateData,
  defaultContactTypes,
  defaultStores,
  defaultLanguage,
  formError,
  onCursorPosition,
}) => {
  const profile = useSelector((state) => state?.login?.profile);

  const contactSelect = [
    { label: 'Clients', id: 'C' },
    { label: 'Professionnels', id: 'P' },
    { label: 'Marketplaces', id: 'M' },
  ];
  const stores = useSelector((state) => state?.static?.stores);
  const dispatch = useDispatch();
  const [storeSelect, setStoreSelect] = useState([]);
  const { dynDatas } = useDynamicData({});

  const handleSetAttachments = (attachments) => {
    setTemplateData({ ...templateData, attachments });
  };

  const handleDeleteAttachment = async (att) => {
    try {
      setTemplateData({
        ...templateData,
        attachments: [
          ...(templateData?.attachments?.filter((a) => a !== att) || []),
          { ...att, status: 'loading' },
        ],
        attachmentsSize: templateData?.attachmentsSize - (att?.file?.size || 0),
      });
      const response = await AxiosConfig.create(
        'delete',
        `/templates/${templateData?.templateId}/attachment/${att?._id}`
      )
        .call()
        .getFullResponse();

      console.log('att', att);
      console.log('response', response);

      if (response?.status >= 300) {
        setTemplateData({
          ...templateData,
          attachments: [
            ...(templateData?.attachments?.filter((a) => a !== att) || []),
            { ...att, status: 'error' },
          ],
          attachmentsSize:
            templateData?.attachmentsSize - (att?.file?.size || 0),
        });
        throw new Error('Error deleting attachment');
      }
      setTemplateData({
        ...templateData,
        attachments: templateData?.attachments?.filter((a) => a !== att),
        attachmentsSize: templateData?.attachmentsSize - (att?.file?.size || 0),
      });
      dispatch(
        setSnackBar({
          open: true,
          message: 'Pièce jointe supprimée avec succès',
          color: 'success',
        })
      );
    } catch (error) {
      console.error('Error deleting attachment', error);
      dispatch(
        setSnackBar({
          open: true,
          message: 'Erreur lors de la suppression de la pièce jointe',
          color: 'error',
        })
      );
    }
  };

  const handleSetAttachmentsSize = (size) => {
    setTemplateData({ ...templateData, attachmentsSize: size });
  };

  const handleFileAddEvent = async (attachmentData) => {
    try {
      const newAttachment = {
        ...attachmentData,
        status: 'loading',
      };

      const newAttachments = [
        ...(templateData?.attachments || []),
        newAttachment,
      ];

      const newAttachmentsSize = newAttachments?.reduce((acc, curr) => {
        return acc + curr?.file?.size;
      }, 0);

      setTemplateData({
        ...templateData,
        attachments: newAttachments,
        attachmentsSize: newAttachmentsSize,
      });

      const attachementToCreate = {
        _id: null,
        content: attachmentData?.content,
        name: attachmentData?.file?.name,
        file: {
          type: attachmentData.file?.type,
          size: attachmentData.file?.size,
          name: attachmentData.file?.name,
          lastModifiedDate: attachmentData.file?.lastModifiedDate,
        },
        link: attachmentData?.link,
        isAtt: true,
      };

      const attachmentUploaded = await AxiosConfig.create(
        'post',
        `/templates/${templateData?.templateId}/attachment`
      )
        .call(attachementToCreate)
        .getResponseData();

      setTemplateData({
        ...templateData,
        attachments: [
          ...(templateData?.attachments || []),
          { ...attachmentUploaded, status: 'success' },
        ],
        attachmentsSize: newAttachmentsSize,
      });
      dispatch(
        setSnackBar({
          open: true,
          message: 'Pièce jointe ajoutée avec succès',
          color: 'success',
        })
      );
    } catch (error) {
      console.error('Error adding attachment', error);
      dispatch(
        setSnackBar({
          open: true,
          message: "Erreur lors de l'ajout de la pièce jointe",
          color: 'error',
        })
      );
    }
  };

  const languageSelect = languages?.map((item) => {
    const value = (
      <Typography>
        <span className={`fi fi-${item?.flag}`} /> - {item?.lang}
      </Typography>
    );
    return { label: item?.lang, id: item?.code2, display: value };
  });
  const body = useMemo(() => {
    return templateData?.content || null;
  }, [templateData]);

  const handleChangeData = (event) => {
    const { id, value } = event.target;
    const nextData = {
      ...templateData,
      [id]: value,
    };

    if (id === 'subject') {
      const labelsInSubject = extractLabelsFromString(value || '');
      const contentLabels = extractLabelsFromJson(templateData?.content);
      const allLabels = new Set([
        ...(labelsInSubject || []),
        ...(contentLabels || []),
      ]);
      const dynamicIds = (dynDatas || [])
        .filter((d) => allLabels.has(d?.label))
        .map((d) => d?._id);
      setTemplateData({ ...nextData, dynamicData: dynamicIds });
    } else {
      setTemplateData(nextData);
    }
  };

  const chooseColor = (value) => {
    let color;
    switch (value) {
      case 'SAV':
        color = green[100];
        break;
      case 'SLO':
        color = blue[100];
        break;
      case 'SCO':
        color = red[100];
        break;
      default:
        color = grey[300];
        break;
    }
    return color;
  };

  const encodeContent = (content) => {
    const newContent = content?.json || content;

    const contentLabels = extractLabelsFromJson(newContent);
    const subjectLabels = extractLabelsFromString(templateData?.subject || '');
    const allLabels = new Set([
      ...(contentLabels || []),
      ...(subjectLabels || []),
    ]);
    const dynamicIds = (dynDatas || [])
      .filter((d) => allLabels.has(d?.label))
      .map((d) => d?._id);

    setTemplateData({
      ...templateData,
      content: newContent,
      dynamicData: dynamicIds,
    });
  };

  const translateContent = (translate) => {
    console.log('here');
    setTemplateData({
      ...templateData,
      content: translate?.body,
      subject: translate?.subject,
    });
  };

  useEffect(() => {
    const storeList = stores
      ?.filter((item) => item?.shortName !== 'Inconnu')
      ?.map((item) => {
        return { label: item.shortName, id: item?._id };
      });
    setStoreSelect(storeList);
  }, [stores]);

  return (
    <Box
      sx={{
        width: '60%',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        margin: '24px 32px',
      }}
    >
      <FormControl
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          gap: 3,
        }}
        value={templateData}
        onChange={handleChangeData}
      >
        {/*Titre du template*/}
        <Box
          sx={{
            width: '50%',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <Typography sx={{ fontWeight: 'bold', width: '35%' }} color="primary">
            Titre du template:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <TextField
              id="name"
              size="small"
              variant="standard"
              required
              value={templateData?.name}
              placeholder="Nouveau template"
            />

            {formError?.name && (
              <FormHelperText id="name-error" error>
                Veuillez remplir ce champ
              </FormHelperText>
            )}
          </Box>
        </Box>

        <Typography sx={{ fontWeight: 'bold', width: '40%' }} color="primary">
          Paramètres
        </Typography>
        {/*Service*/}
        <Box
          sx={{
            width: '50%',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 3,
          }}
        >
          <SupportAgentIcon fontSize="medium" />
          <Typography sx={{ width: '30%' }}>Service:</Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              backgroundColor: chooseColor(profile?.roles[0]?.service?.code),
              borderRadius: 2,
            }}
          >
            <Typography sx={{ px: 2, py: 1, fontSize: 14 }}>
              {profile?.roles[0]?.service?.code || 'Pas de service'}
            </Typography>
          </Box>
        </Box>
        {/*Contact type*/}
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: 3,
          }}
        >
          <GroupIcon fontSize="medium" />
          <Typography sx={{ width: '15%' }}>Type de client:</Typography>
          <Box
            sx={{
              minWidth: '40%',
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
            }}
          >
            <FilterSelect
              props={{ minWidth: '100%' }}
              options={contactSelect}
              setFormData={setTemplateData}
              defaultValue={defaultContactTypes}
              dataField={'contactTypes'}
            />
          </Box>
        </Box>
        {/*Stores*/}
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: 3,
          }}
        >
          <ShoppingCartIcon fontSize="medium" />
          <Typography sx={{ width: '15%' }}>Magasins:</Typography>
          <Box
            sx={{
              minWidth: '40%',
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
            }}
          >
            <FilterSelect
              props={{ minWidth: '100%' }}
              options={storeSelect}
              setFormData={setTemplateData}
              defaultValue={defaultStores}
              dataField={'stores'}
            />
          </Box>
        </Box>
        {/*Language*/}
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',

            alignItems: 'center',
            gap: 3,
          }}
        >
          <TranslateIcon fontSize="medium" />
          <Typography sx={{ width: '15%' }}>Langue:</Typography>

          <Box
            sx={{
              minWidth: '40%',
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box
              sx={{
                minWidth: '100%',
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
              }}
            >
              <MonoFilterSelect
                props={{ minWidth: '100%' }}
                options={languageSelect}
                setFormData={setTemplateData}
                defaultValue={defaultLanguage}
                dataField={'language'}
              />
            </Box>
          </Box>
        </Box>
        {formError?.settings && (
          <FormHelperText id="setting-error" error>
            Veuillez choisir au moins une option pour chaque paramètres
          </FormHelperText>
        )}

        <Typography sx={{ fontWeight: 'bold', width: '40%' }} color="primary">
          Message:
        </Typography>
        {/*Translate*/}
        <TranslateTemplate
          subject={templateData?.subject}
          content={templateData?.content}
          changeContent={translateContent}
        />
        {/*Subject*/}
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography sx={{ fontWeight: 'bold' }}>Objet:</Typography>
          <TextField
            id="subject"
            size="small"
            variant="outlined"
            required
            fullWidth
            value={templateData?.subject || ''}
            placeholder="Objet du mail..."
          />
        </Box>
        {/*Message content*/}
        <ComponentRTE
          variant="default"
          content={body}
          onChange={encodeContent}
          onCursorPosition={onCursorPosition}
        />
        <Dropzone
          type="template"
          templateId={templateData?.templateId}
          attachments={templateData?.attachments}
          setAttachments={handleSetAttachments}
          deleteAttachment={handleDeleteAttachment}
          attachmentsSize={templateData?.attachmentsSize}
          setTotalAttachmentsSize={handleSetAttachmentsSize}
          onFileAdd={handleFileAddEvent}
        />
      </FormControl>
    </Box>
  );
};
